// Shared WebRTC ICE configuration for every call surface (Messenger, Astrology rooms, Ride VoIP).
// Public endpoint = STUN only. TURN is never public: it is fetched per call from the authenticated
// /config/turn-credentials (short-lived, conversation-scoped) and only when the server has it enabled.
import { api } from "@/lib/api";

export const DEFAULT_ICE = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

const asList = (u) => (Array.isArray(u) ? u : [u]).filter((x) => typeof x === "string");

// Public list may only contain credential-free STUN entries (defence in depth).
export function sanitizePublicIce(list) {
  const clean = (Array.isArray(list) ? list : [])
    .filter((s) => s && !s.username && !s.credential && asList(s.urls).length && asList(s.urls).every((u) => u.startsWith("stun:")))
    .map((s) => ({ urls: s.urls }));
  return clean.length ? clean : DEFAULT_ICE.map((s) => ({ ...s }));
}

// Adds short-lived TURN entries only when the server explicitly enabled and returned them.
export function mergeIce(publicList, turnResp) {
  const base = sanitizePublicIce(publicList);
  if (!turnResp || turnResp.enabled !== true || !Array.isArray(turnResp.iceServers)) return base;
  const turn = turnResp.iceServers.filter((s) => s && s.username && s.credential &&
    asList(s.urls).length && asList(s.urls).every((u) => u.startsWith("turn:") || u.startsWith("turns:")));
  return [...base, ...turn];
}

export async function loadPublicIce() {
  try {
    const r = await api.get("/config/ice-servers");
    return sanitizePublicIce(r.data?.iceServers);
  } catch {
    return DEFAULT_ICE.map((s) => ({ ...s }));
  }
}

export async function iceForCall(convId) {
  const pub = await loadPublicIce();
  if (!convId) return pub;
  try {
    const r = await api.post("/config/turn-credentials", { conv_id: convId });
    return mergeIce(pub, r.data);
  } catch {
    return pub;     // 404/429/network → STUN-only, the call still proceeds
  }
}
